import React, { useMemo, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const TemplateCard = ({ template, chosen, onPick, getScenarioLabel }) => (
  <button
    type="button"
    onClick={onPick}
    className={`w-full text-left p-4 rounded-2xl border transition
      ${chosen ? 'border-blue-500 ring-2 ring-blue-200' : 'border-border hover:border-blue-300'}`}
  >
    <div className="text-sm text-muted-foreground float-right font-bold">
      Step {template.step_number}
    </div>
    <h4 className="font-medium">{template.name}</h4>
    {/* show label instead of raw value */}
    <p className="text-sm text-muted-foreground">{getScenarioLabel(template.scenario)}</p>
    <p className="text-sm font-medium">{template.subject}</p>
    <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
      {template.tone && <Badge variant="outline" className="text-xs">{template.tone}</Badge>}
      {template.lastModified && <span>Modified {template.lastModified}</span>}
    </div>
  </button>
);

const TemplateSelectorModal = ({
  isOpen,
  onClose,
  templates = [],
  newCampaign = {},
  scenarios = [],                 // ← добавили
  selectedByStep = {},
  setSelectedByStep = () => {},
}) => {
  const emailCount = Number(newCampaign.emailCount) || 0;

  // normalize scenario: can be 'value' string or {value,label}
  const scenarioValue = typeof newCampaign.scenario === 'object'
    ? newCampaign.scenario?.value
    : newCampaign.scenario;

  // map value -> label
  const scenarioLabelMap = useMemo(() => {
    const map = {};
    (scenarios || []).forEach(s => { map[String(s.value)] = s.label; });
    return map;
  }, [scenarios]);

  const getScenarioLabel = useCallback(
    (val) => scenarioLabelMap[String(val)] || String(val || ''),
    [scenarioLabelMap]
  );

  // Filter by chosen scenario (value) and allowed steps
  const filtered = useMemo(() => {
    if (!Array.isArray(templates) || !scenarioValue) return [];
    return templates
      .filter(t => String(t?.scenario) === String(scenarioValue))
      .filter(t => {
        const step = Number(t?.step_number) || 0;
        return step >= 1; // больше не ограничиваем по emailCount
      })
      .sort((a, b) => Number(a.step_number) - Number(b.step_number));
  }, [templates, scenarioValue, emailCount]);


  // Group by step
  const byStep = useMemo(() => {
    const acc = {};
    for (const t of filtered) {
      const step = String(t.step_number ?? 1);
      (acc[step] ||= []).push(t);
    }
    return acc;
  }, [filtered]);

  const steps = useMemo(
    () => Object.keys(byStep).sort((a, b) => Number(a) - Number(b)),
    [byStep]
  );

  // Toggle select / unselect
  const handlePickTemplate = useCallback((step, templateId) => {
    setSelectedByStep(prev => {
      const current = String(prev[step] ?? '');
      if (current === String(templateId)) {
        const updated = { ...prev };
        delete updated[step];
        return updated;
      }
      return { ...prev, [step]: templateId };
    });
  }, [setSelectedByStep]);

  const clearStep = useCallback((step) => {
    setSelectedByStep(prev => {
      if (!(step in prev)) return prev;
      const updated = { ...prev };
      delete updated[step];
      return updated;
    });
  }, [setSelectedByStep]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!max-w-[1000px] w-full" style={{ zIndex: 1000 }}>
        <DialogHeader>
          <DialogTitle>
            Choose templates • {scenarioValue ? getScenarioLabel(scenarioValue) : 'Pick scenario'}
          </DialogTitle>
          <DialogDescription>
            Pick at most one template for each step. Click again to unselect.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
          {(!scenarioValue || steps.length === 0) ? (
            <p className="text-center text-muted-foreground">
              {scenarioValue ? 'No templates found for this scenario.' : 'Select a scenario first.'}
            </p>
          ) : steps.map(step => (
            <div key={step}>
              <div className="mb-2 flex items-center gap-3">
                <h4 className="font-semibold">Step {step}</h4>
                {selectedByStep[step] && (
                  <>
                    <Badge variant="secondary">Selected ID: {selectedByStep[step]}</Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => clearStep(step)}
                      className="ml-1"
                    >
                      Clear
                    </Button>
                  </>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {byStep[step].map(t => {
                  const chosen = String(selectedByStep[step]) === String(t.id);
                  return (
                    <Card key={t.id} className="p-0 border-none">
                      <TemplateCard
                        template={t}
                        chosen={chosen}
                        onPick={() => handlePickTemplate(step, t.id)}
                        getScenarioLabel={getScenarioLabel}
                      />
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateSelectorModal;